import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { GUIDE_TOPICS } from '../content/topics';
import { GUIDE_TOURS, type GuideTourId } from '../content/tours';
import { useGuideStore } from '../store/guideStore';
import { waitForFirstVisibleStep } from '../utils/tourSteps';
import { GuideOverlay } from '../components/GuideOverlay';
import { GuideStepPopover } from '../components/GuideStepPopover';
import { useAnchorRect } from './useAnchorRect';
import { useVisibleSteps } from './useVisibleSteps';
import { GuideContext, type GuideContextValue } from './guideContext';

export function GuideProvider({ children }: { children: ReactNode }) {
  const [activeTourId, setActiveTourId] = useState<GuideTourId | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const markSeen = useGuideStore((state) => state.markSeen);
  const setEnabled = useGuideStore((state) => state.setEnabled);

  // Only the newest start() may open a tour: moving between two guided screens
  // inside the polling window would otherwise let the first screen's poll
  // resolve last and open the tour the manager already left.
  const startToken = useRef(0);
  const cancelPoll = useRef<(() => void) | null>(null);
  const polledTourId = useRef<GuideTourId | null>(null);
  useEffect(() => () => cancelPoll.current?.(), []);

  const tour = activeTourId ? GUIDE_TOURS[activeTourId] : null;
  const step = tour ? tour.steps[stepIndex] : null;
  const rect = useAnchorRect(step ? step.topicId : null);

  const visible = useVisibleSteps(tour?.steps ?? null, stepIndex);
  const position = visible.indexOf(stepIndex);
  const nextIndex = visible[position + 1] ?? -1;
  const prevIndex = visible[position - 1] ?? -1;

  const close = useCallback(
    (markAsSeen: boolean) => {
      if (markAsSeen && activeTourId) markSeen(activeTourId);
      setActiveTourId(null);
    },
    [activeTourId, markSeen],
  );

  const start = useCallback((tourId: GuideTourId) => {
    const target = GUIDE_TOURS[tourId];
    if (!target) return;

    cancelPoll.current?.();
    const token = (startToken.current += 1);
    polledTourId.current = tourId;

    // A tour whose anchors never showed up is left unseen, so a slow render
    // does not burn it for good.
    cancelPoll.current = waitForFirstVisibleStep(target.steps, (index) => {
      if (index === -1 || token !== startToken.current) return;
      setActiveTourId(tourId);
      setStepIndex(index);
    });
  }, []);

  // Checked against the latest state, not this render's: a screen that goes
  // away ends its own tour and never one that another screen opened.
  const stop = useCallback((tourId: GuideTourId) => {
    if (polledTourId.current === tourId) cancelPoll.current?.();
    setActiveTourId((current) => (current === tourId ? null : current));
  }, []);

  const next = useCallback(() => {
    if (nextIndex === -1) close(true);
    else setStepIndex(nextIndex);
  }, [close, nextIndex]);

  const prev = useCallback(() => {
    if (prevIndex !== -1) setStepIndex(prevIndex);
  }, [prevIndex]);

  const skip = useCallback(() => close(true), [close]);

  const disableAll = useCallback(() => {
    setEnabled(false);
    close(true);
  }, [close, setEnabled]);

  const value = useMemo<GuideContextValue>(
    () => ({ start, stop }),
    [start, stop],
  );

  const topic = step ? GUIDE_TOPICS[step.topicId as keyof typeof GUIDE_TOPICS] : null;

  return (
    <GuideContext.Provider value={value}>
      {children}
      {tour && step && topic && (
        <>
          <GuideOverlay rect={rect} />
          <GuideStepPopover
            topic={topic}
            rect={rect}
            placement={step.placement}
            stepNumber={position + 1}
            totalSteps={visible.length}
            isFirst={prevIndex === -1}
            isLast={nextIndex === -1}
            onNext={next}
            onPrev={prev}
            onSkip={skip}
            onDisableAll={disableAll}
          />
        </>
      )}
    </GuideContext.Provider>
  );
}
